import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import CabiBubble from "../../components/Kids/CabiBubble";
import StatCard from "../../components/Kids/StatCard";
import Pill from "../../components/Kids/Pill";
import ProgressBar from "../../components/Kids/ProgressBar";
import EmptyState from "../../components/Kids/EmptyState";
import Skeleton from "../../components/Kids/Skeleton";

const API = process.env.REACT_APP_API_URL;

function SimpleBarChart({ data, valueKey, label, color }) {
  const max  = Math.max(...data.map(d => d[valueKey] || 0), 1);
  const barW = Math.max(4, Math.min(20, 300 / data.length));
  return (
    <div className="bg-white rounded-card border border-cardline shadow-card p-4">
      <h3 className="text-[13px] font-body font-extrabold text-navy mb-3">{label}</h3>
      <svg viewBox={`0 0 ${data.length * (barW + 2)} 100`} className="w-full" style={{ maxHeight: 120 }}>
        {data.map((d, i) => {
          const h = ((d[valueKey] || 0) / max) * 80;
          return <rect key={i} x={i * (barW + 2)} y={80 - h} width={barW} height={h} rx={2} fill={color} opacity={0.8} />;
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-faint mt-1 font-body">
        <span>{data[0]?.date?.slice(5) || ""}</span>
        <span>{data[data.length - 1]?.date?.slice(5) || ""}</span>
      </div>
    </div>
  );
}

const PERIOD_OPTIONS = [
  { label: "7 days",  value: 7  },
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
];

export default function KidsReports() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token    = localStorage.getItem("kids_parent_token");

  const [children, setChildren] = useState([]);
  const [active, setActive]     = useState(0);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(false);
  const [period, setPeriod]     = useState(30);
  const [history, setHistory]   = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadDashboard = useCallback(() => {
    if (!token) { navigate("/kids/login"); return; }
    setLoading(true);
    setError(false);
    axios.get(`${API}/api/kids/parent/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setChildren(res.data?.data?.children || []))
      .catch((err) => {
        if (err.response?.status === 401) { navigate("/kids/login"); return; }
        toast.error("Could not load reports.");
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [token, navigate]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const child = children[active];

  // Fetch progress history when child or period changes
  useEffect(() => {
    if (!child?.id || !token) return;
    setHistoryLoading(true);
    axios.get(`${API}/api/kids/progress/${child.id}/history`, {
      params:  { days: period },
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setHistory(r.data?.data || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [child?.id, period, token]);

  return (
    <div className="min-h-screen font-body text-ink" style={{ background: "linear-gradient(150deg, #FFF6EE 0%, #FFF9F4 55%, #ffffff 100%)" }}>
      <div className="max-w-4xl mx-auto p-4 sm:p-8">
        <div className="flex items-center justify-between mb-2">
          <Link to="/kids/parent/dashboard" className="text-muted hover:text-brand text-sm font-body font-semibold transition">← Dashboard</Link>
          <Link to="/kids" className="font-display font-semibold text-xl text-brand">Cabil <span className="text-gold">Kids</span></Link>
        </div>

        <div className="mb-8 mt-4">
          <CabiBubble
            mood="happy"
            theme="light"
            size={72}
            text={
              <>
                <div className="font-display font-semibold text-[20px] text-ink">{t("kids.reports.title")} 📊</div>
                <div className="font-body text-[13px] text-muted mt-0.5">
                  See how {child?.name || "your child"} is growing, one game at a time.
                </div>
              </>
            }
          />
        </div>

        {loading ? (
          <ReportsSkeleton />
        ) : error ? (
          <div className="bg-white rounded-card-lg shadow-card border border-cardline">
            <EmptyState
              mood="wrong"
              theme="light"
              title="Oops — no internet!"
              subtitle="We couldn't load the report. Check your connection and try again."
              cta="Retry"
              onCta={loadDashboard}
            />
          </div>
        ) : children.length === 0 ? (
          <div className="bg-white rounded-card-lg shadow-card border border-cardline">
            <EmptyState
              mood="thinking"
              theme="light"
              emoji="🧒"
              title="No children yet"
              subtitle="Add a child to start tracking their learning journey."
              cta="Add Child 🌟"
              onCta={() => navigate("/kids/parent/add-child")}
            />
          </div>
        ) : (
          <>
            {/* Child selector */}
            {children.length > 1 && (
              <div className="flex gap-3 mb-6 overflow-x-auto pb-1">
                {children.map((c, i) => (
                  <Pill key={c.id} active={i === active} onClick={() => setActive(i)}>
                    <span>🧒</span> {c.name}
                  </Pill>
                ))}
              </div>
            )}

            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard emoji="⭐" value={child.total_xp ?? 0} label={t("kids.reports.totalXp")} />
              <StatCard emoji="🎮" value={child.games_completed ?? 0} label="Games Completed" />
              <StatCard emoji="🔥" value={child.streak_days ?? 0} label="Day Streak" />
              <StatCard emoji="⏱️" value={`${child.time_spent_mins ?? 0}m`} label={t("kids.reports.timeSpent")} />
            </div>

            {/* Level progress */}
            <div className="bg-white rounded-card-lg p-6 shadow-card border border-cardline">
              <div className="flex items-center justify-between mb-3 gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-12 h-12 shrink-0 rounded-2xl flex items-center justify-center text-white font-display font-semibold text-lg shadow-card"
                    style={{ background: "linear-gradient(150deg,#FBB03B,#EE5F2B)" }}>
                    {child.level ?? 1}
                  </div>
                  <div className="min-w-0">
                    <p className="font-body font-extrabold text-ink truncate">{child.name} · {t("kids.reports.level")} {child.level ?? 1}</p>
                    <p className="text-faint text-sm">{child.grade_label || ""}</p>
                  </div>
                </div>
                <span className="text-sm font-body font-semibold text-brand shrink-0">{(child.total_xp ?? 0) % 100}/100 XP</span>
              </div>
              <div className="rounded-full p-[2px]" style={{ background: "linear-gradient(90deg,#FBB03B,#EE5F2B)" }}>
                <ProgressBar value={((child.total_xp ?? 0) % 100) / 100} className="h-3" />
              </div>
              <p className="text-muted text-sm mt-4">
                {child.name} has earned <span className="font-bold text-brand">{child.total_xp ?? 0} XP</span> and
                completed <span className="font-bold text-brand">{child.games_completed ?? 0} games</span>. Keep it up! 🎉
              </p>
            </div>

            {/* Period selector */}
            <div className="flex gap-2 mt-6 mb-4">
              {PERIOD_OPTIONS.map(opt => (
                <Pill key={opt.value} active={period === opt.value} onClick={() => setPeriod(opt.value)}>
                  {opt.label}
                </Pill>
              ))}
            </div>

            {/* Progress charts */}
            {historyLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-[168px] w-full" rounded="16px" />
                ))}
              </div>
            ) : history.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <SimpleBarChart data={history} valueKey="xp_earned" label="Daily XP Earned" color="#EE5F2B" />
                <SimpleBarChart data={history} valueKey="games_played" label="Games Played" color="#F59E0B" />
                <SimpleBarChart data={history} valueKey="time_spent" label="Time Spent (min)" color="#10B981" />
              </div>
            ) : (
              <div className="bg-white rounded-card border border-cardline shadow-card">
                <EmptyState
                  mood="thinking"
                  theme="light"
                  emoji="📊"
                  title={t("kids.reports.noActivity")}
                  subtitle="Play some games to see progress here!"
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Loading skeleton: stat tiles + level card + charts shaped like the real layout ── */
function ReportsSkeleton() {
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" rounded="16px" />
        ))}
      </div>
      <Skeleton className="h-32 w-full mb-6" rounded="20px" />
      <div className="flex gap-2 mb-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-20" rounded="9999px" />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-[168px] w-full" rounded="16px" />
        ))}
      </div>
    </div>
  );
}
